<template>
  <div
    :class="{
      'post-preview-section': true,
      'post-preview-section--disabled': isSavingScheduleDate,
    }"
  >
    <div class="post-preview-section__post-snippet-wrap">
      <!-- Status stripe -->
      <CollabStatusStripe
        v-if="props.isStatusAndSocialsPanelVisible && !props.onlyAtlasReadonlyFieldsVisible && props.post.status"
        class="post-preview-section__status-stripe"
        :color="getStatusColor"
        :isWithCheckIcon="props.post.status === SocialPostStatus.Approved"
        :text="getStatusText()"
      />

      <FbPostPreview
        :class="{
          'post-preview-section__post-snippet': true,
          'post-preview-section__post-snippet--translucent': props.post.markedUsed && isUsedPostObvious,
        }"
        :style="props.isUnusedPost ? unusedPostStyle : undefined"
        :images-list="props.assetIds"
        :lightboxKey="props.lightboxKey"
        :debugData="props.debugData"
      >
        <template #textarea>
          <textarea
            v-if="props.isWithTextarea"
            class="post-preview-section__post-snippet-textarea"
            :value="props.caption"
            @input="$event => emit('update:caption', ($event.target as HTMLInputElement).value)"
            ref="domRefTextareaFb"
            placeholder="Caption"
          />
          <ClampedText v-else style="display: block; white-space: pre-wrap; font: 14px/18px sans-serif" :visibleLines="5">
            {{ props.caption }}
          </ClampedText>
        </template>
      </FbPostPreview>

      <!-- Used mark -->
      <div v-if="props.post.markedUsed" class="used-mark post-preview-section__used-mark">
        <div v-if="!props.post.postUrl" class="used-mark__header">
          <h6 class="global-h6 used-mark__title">Where was it used?</h6>

          <InfoIconWithTooltip class="used-mark__icon" icon="trending-up_2-5" :width="300" style="position: relative"
            >Let us know the url of the post.<br />We're laying the groundwork to track performance so we can help you see what's working!
          </InfoIconWithTooltip>
        </div>

        <template v-if="props.post.postUrl">
          <span class="used-mark__link">Used for: <span v-html="linkifyStr(props.post.postUrl, { ...globalRoot.linkifyOptions, truncate: 25 })"></span></span>
        </template>

        <div v-else class="used-mark__add-link-form">
          <input class="global-text-input global-text-input--tiny used-mark__text-input" type="text" placeholder="https://facebook.com/example" v-model="_postUrl" />
          <SrpButton class="used-mark__add-button" size="tiny" @click="upsertPostUsageUrl" :isDisabled="isSavingPostUrl">
            <template v-if="isSavingPostUrl" #icon><LoadingIcon :size="14" /></template>
            Add
          </SrpButton>
        </div>
      </div>
      <!-- / Used mark -->

      <div v-if="slots.footer" style="padding: 10px 12px 0">
        <div class="post-preview-section__footer-divider"></div>
        <slot name="footer" />

        <div v-if="props.isPostScheduleVisible">
          <NoteWithIcon v-if="isScheduleWarningVisible" size="tiny" color="red" style="margin-bottom: 10px">
            <template #text
              ><b>Must be scheduled at least {{ POST_APPROVAL_MINUTES_AHEAD }} minutes in the future</b></template
            >
          </NoteWithIcon>

          <!-- Post schedule field -->
          <div v-if="!props.post.postedDetails?.length" class="post-schedule-field post-preview-section__post-schedule-field">
            <!-- Social logos row -->
            <div class="social-logos-row post-preview-section__social-logos-row">
              <template v-if="props.post.postToAccounts?.length">
                <ul class="social-logos-row__ul">
                  <li class="social-logos-row__logo-wrap" v-for="socialLogo in props.post.postToAccounts" :key="socialLogo">
                    <img
                      :class="['social-logos-row__logo', { 'social-logos-row__logo--inactive': props.isUnusedPost }]"
                      :src="`/images/social-logo-${socialLogo.toLowerCase()}.svg`"
                      :alt="socialLogo"
                    />
                  </li>
                  <LinkWithIcon v-if="props.isSocialsListEditable && !props.isUnusedPost" color="gray" isDottedUnderline style="flex-shrink: 0" @click="isPickSocialsModalVisible = true">
                    <template #icon><IconEmbedded name="pencil_2" :size="16" /></template>
                  </LinkWithIcon>
                </ul>
              </template>

              <template v-else>
                <ul class="social-logos-row__ul">
                  <li class="social-logos-row__logo-wrap">
                    <img class="social-logos-row__logo social-logos-row__logo--inactive" src="/images/social-logo-instagram.svg" alt="Instagram" />
                  </li>
                  <li class="social-logos-row__logo-wrap">
                    <img class="social-logos-row__logo social-logos-row__logo--inactive" src="/images/social-logo-facebook.svg" alt="Facebook" />
                  </li>
                  <TooltipWrapper v-if="!props.isUnusedPost">
                    <template #hoverableElement>
                      <LinkWithIcon isDottedUnderline @click="isPickSocialsModalVisible = true" color="light-gray" tooltip="Connect your Accounts">
                        <template #icon><IconEmbedded name="plus_2-5" :size="18" /></template>
                      </LinkWithIcon>
                    </template>
                    <template #tooltip>Connect your Accounts</template>
                  </TooltipWrapper>
                </ul>
              </template>

              <PickSocialNetworksModal
                v-if="isPickSocialsModalVisible"
                v-model:isVisible="isPickSocialsModalVisible"
                :allCustomerSocialChannels="props.customerSocialChannels"
                :selectedSocialChannels="props.post.postToAccounts"
                :post="props.post"
                @save="selectedSocialChannels => emit('updatePostToAccounts', selectedSocialChannels)"
                @addSocialAccount="emit('addSocialAccount')"
              />
            </div>
            <!-- / Social logos row -->

            <VueDatePicker
              class="post-schedule-field__invisible-date-field-overlay"
              ref="domRefPostScheduleInput"
              v-model="postScheduleDateTime"
              :minDate="props.postScheduleMinDate"
              :maxDate="props.postScheduleMaxDate"
              teleport="body"
              :is24="false"
              timePickerInline
              :config="{ setDateOnMenuClose: true, keepActionRow: true }"
              @update:modelValue="async () => await updatePostScheduleDate()"
            />

            <template v-if="props.post.scheduledToPostAt?.localDateTime">
              <span style="white-space: nowrap">
                {{ formatScheduledDate(props.post.scheduledToPostAt.localDateTime) }} {{ getTimeZoneAbbreviation() }}
                <LinkWithIcon color="gray" isDottedUnderline style="margin-left: -1px" @click="() => domRefPostScheduleInput.openMenu()">
                  <template #icon><IconEmbedded name="pencil_2" :size="16" /></template>
                </LinkWithIcon>
              </span>
            </template>

            <template v-else>
              <LinkWithIcon isDottedUnderline @click="() => domRefPostScheduleInput.openMenu()">
                <template #icon><IconEmbedded name="calendar_2" :size="18" /></template>
                <span>Schedule post</span>
              </LinkWithIcon>
            </template>
          </div>
          <!-- / Post schedule field -->

          <!-- "Posted on" field -->
          <div v-else-if="props.post.postedDetails?.length && props.post.scheduledToPostAt" class="posted-on-field post-preview-section__posted-on-field">
            <div class="posted-on-field__icons-row">
              <LinkWithIcon
                class="posted-on-field__icon"
                v-for="postedItemDetails in props.post.postedDetails.filter(postedItemDetails => postedItemDetails.postUrl)"
                :key="postedItemDetails.socialChannel"
                tag="a"
                color="blue"
                target="_blank"
                :href="postedItemDetails.postUrl"
              >
                <template #icon><IconEmbedded :name="postedItemDetails.socialChannel" :size="20" /></template>
              </LinkWithIcon>
            </div>
            <!--<div class="posted-on-field__text">{{ `Posted on ${DayjsExtended(props.post.postedOn).format("dddd [at] h:mma")}` }}</div>-->
            <div class="posted-on-field__text">{{ `Posted on ${DayjsExtended(props.post.scheduledToPostAt.localDateTime).format("dddd [at] h:mma")}` }} {{ getTimeZoneAbbreviation() }}</div>
          </div>
          <!-- / "Posted on" field -->
        </div>
      </div>

      <template v-else>
        <!-- Share and dislike buttons -->
        <div v-if="props.isWithShareAndDislikeButtons" class="share-and-dislike-buttons post-preview-section__share-and-dislike-buttons">
          <SrpButton class="share-and-dislike-buttons__share-button" size="small" :fill="props.post.markedUsed ? 'outlined' : 'solid'" @click="emit('clickCustomizeAndShare')">
            {{ props.readonlyMode ? "View readonly" : props.post.markedUsed ? "Use Again" : "Customize & Use" }}
          </SrpButton>

          <SrpButton :isDisabled="props.readonlyMode" class="share-and-dislike-buttons__dislike-button" size="small" color="gray" fill="outlined" @click="emit('clickDislike')">
            <template #icon><IconEmbedded name="dislike_2" :size="21" /></template>
          </SrpButton>
        </div>
        <!-- / Share and dislike buttons -->

        <!-- What post is based on -->
        <div v-if="props.isWhatPostIsBasedOnVisible && props.post.contextType && !props.onlyAtlasReadonlyFieldsVisible" class="post-preview-section__what-post-is-based-on">
          Post is based on {{ getCreatedFromPrefix(props.post.contextType) }}
          <!-- <IconEmbedded style="position: relative; top: 4px" :name="getCreatedFromIconName(props.post.contextType)" :size="18" color="rgba(12, 135, 138, 1)" /> -->
          {{ getCreatedFromFriendlyName(props.post.contextType) }}
        </div>
        <!-- / What post is based on -->
      </template>

      <div v-if="props.showDebug" style="margin-top: 12px">
        <EvalResults :evalResults="props.debugData?.evalResults" />
        <div style="display: flex; align-items: center; gap: 8px; margin-top: 5px">
          <button v-if="props.post" class="ui mini button" @click.prevent="historyModalRef?.openModal()">History</button>
          <template v-if="props.debugData">
            <button class="ui mini button" @click.prevent="debugModalRef?.openModal()">Debug</button>
            <LinkWithIcon v-if="props.debugData?.evalResults" isDottedUnderline @click="reRunEvals">
              <template #icon><IconEmbedded name="zap_2" :size="18" /></template>
              <span>Redo Evals</span>
            </LinkWithIcon>
            <IconEmbedded v-if="showEvalsRequestedIndicator" name="check-circle_2-5" :size="18" color="rgba(0, 200, 0, 1)" style="transition: opacity 0.3s" />
          </template>
        </div>
        <div v-if="props.post?.id" style="display: flex; align-items: center; gap: 8px; margin-top: 8px; font-size: 12px; color: rgba(0, 0, 0, 0.65); margin-left: 5px;">
          <CopyTextNew>{{ props.post?.id }}</CopyTextNew>
        </div>
      </div>
    </div>
    <!--<TwPostPreview class="post-preview-section__snippet" />-->

    <!-- Debug Modal -->
    <ImageSelectionDebugModal ref="debugModalRef" :debugData="props.debugData" />
    <!-- Event History Modal -->
    <PostEventHistoryModal v-if="props.post" ref="historyModalRef" :customerId="props.post.customerId" :postId="props.post.id" />
  </div>
</template>
<script setup lang="ts">
import { inject, Ref, ref, onMounted, watch, useSlots, computed } from "vue";
import { useRouter } from "vue-router";
import linkifyStr from "linkify-string";
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";
import DayjsExtended from "@logic/DayjsExtended";
import { isScheduledDateTooSoon, POST_APPROVAL_MINUTES_AHEAD, MS_TO_SHOW_WARNING } from "@logic/ContentCalendarValidator";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import FbPostPreview from "./FbPostPreview.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import ClampedText from "@components/ui/ClampedText.vue";
import ImageSelectionDebugModal from "@components/ImageSelectAIDebug/ImageSelectionDebugModal.vue";
import PostEventHistoryModal from "@components/SocialPostPreview/PostEventHistoryModal.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import CollabStatusStripe from "@components/CollabStatusStripe.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import PickSocialNetworksModal from "@components/SocialPostPreview/PickSocialNetworksModal.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import EvalResults from "@components/SocialPostPreview/EvalResults.vue";
import CopyTextNew from "@components/CopyTextNew.vue";
import TooltipWrapper from "@components/ui/TooltipWrapper.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";

// Types
import { ScreenSize } from "@contracts/screenSize";
import { ImageSelectAIDebugData, SocialMediaPostData, SocialPostTypeId, SocialPostStatus, UpdatePostScheduledAtResponse } from "@contracts/shareStop";
import { IconName } from "@contracts/IconName";
import { SocialChannels } from "@contracts/socialNetworks";
import type { SocialChannelData } from "@contracts/socialMediaSettings";

// Global variables & injections
const screenSize = inject("screenSize") as Ref<ScreenSize>;
const globalRoot = inject("globalRoot") as any;

// Router
const _router = useRouter();

// DOM refs ===================================================================
const domRefPostScheduleInput = ref<any>(null);

const slots = useSlots();

defineOptions({ name: "SocialPostPreview" });

const props = withDefaults(
  defineProps<{
    assetIds: Array<string>;
    caption: string;
    isWithTextarea?: boolean;
    isWithShareAndDislikeButtons?: boolean;
    lightboxKey: string;
    showDebug?: boolean;
    readonlyMode?: boolean;
    debugData?: ImageSelectAIDebugData | null;
    post?: SocialMediaPostData | null;
    isWhatPostIsBasedOnVisible?: boolean; // ex "Post is based on the Creator Visit"
    isStatusAndSocialsPanelVisible?: boolean;
    isPostScheduleVisible?: boolean;
    postScheduleMinDate?: string;
    postScheduleMaxDate?: string;
    isSocialsListEditable?: boolean;
    customerSocialChannels?: Array<SocialChannelData>;
    onlyAtlasReadonlyFieldsVisible?: boolean; // Used by the dashboard
    isUnusedPost?: boolean; // When true, dims certain sections and shows "Unused" status
  }>(),
  {
    assetIds: () => [],
    caption: "",
    isWithTextarea: false,
    isWithShareAndDislikeButtons: true,
    lightboxKey: "",
    showDebug: false,
    debugData: null,
    readonlyMode: false,
    post: null,
    isWhatPostIsBasedOnVisible: false,
    isStatusAndSocialsPanelVisible: false,
    isPostScheduleVisible: false,
    postScheduleMinDate: "",
    postScheduleMaxDate: "",
    isSocialsListEditable: false,
    customerSocialChannels: () => [],
    onlyAtlasReadonlyFieldsVisible: false,
    isUnusedPost: false,
  }
);

const emit = defineEmits<{
  (e: "clickCustomizeAndShare"): void;
  (e: "update:caption", value: string): void;
  (e: "clickDislike"): void;
  (e: "updatePostUrl", value: string, etag: string): void;
  (e: "updatePostSchedule", response: UpdatePostScheduledAtResponse): void;
  (e: "updatePostToAccounts", selectedSocialChannels: Array<SocialChannels>): void;
  (e: "addSocialAccount"): void;
}>();

// DOM refs ===================================================================
const domRefTextareaFb = ref<any>(null);
const debugModalRef = ref<InstanceType<typeof ImageSelectionDebugModal> | null>(null);
const historyModalRef = ref<InstanceType<typeof PostEventHistoryModal> | null>(null);

// Define expose ==============================================================
defineExpose({
  domRefTextareaFb,
});

// I'm leaning toward making used posts not that different from non-used ones.
// Flipping this to true will invert that change
const isUsedPostObvious = true;

// Style applied to sections when post is unused (single source of truth for opacity)
const unusedPostStyle = { opacity: 0.5 };

const getStatusColor = computed(() => {
  if (props.post?.deletedDate) return "red";
  if (props.post?.status === SocialPostStatus.Approved || props.post?.status === SocialPostStatus.Published) return "green";
  return "gray";
});

function getCreatedFromFriendlyName(contextType: SocialPostTypeId): string | null {
  switch (contextType) {
    case "collabInput":
      return "Creator Visit";
    case "adventure":
      return "Adventure";
    case "location":
      return "Visit Location";
    case "customer-upload":
      return "Your Uploads";
    case "community-upload":
      return "Community Uploads";
    default:
      return null;
  }
}

function getCreatedFromPrefix(contextType: SocialPostTypeId): string {
  switch (contextType) {
    case "collabInput":
      return "a";
    case "adventure":
      return "an";
    case "location":
      return "a";
    default:
      return "";
  }
}

function getCreatedFromIconName(contextType: SocialPostTypeId): IconName {
  switch (contextType) {
    case "collabInput":
      return "briefcase_2";
    case "adventure":
      return "compass_2";
    case "location":
      return "map-pin_2";
    case "customer-upload":
      return "upload_2";
    case "community-upload":
      return "users_2";
    default:
      return "upload_2";
  }
}

// Post url local copy ========================================================
const _postUrl = ref<string>("");

onMounted(() => {
  _postUrl.value = props.post.postUrl;
});

watch(
  () => props.post.postUrl,
  () => (_postUrl.value = props.post.postUrl)
);

// Upsert post usage url ======================================================
const isSavingPostUrl = ref<boolean>(false);

async function upsertPostUsageUrl() {
  isSavingPostUrl.value = true;

  const updatedPost = await ShareStopSummaryRepo.upsertPostUsageLink(props.post.customerId, props.post.id, _postUrl.value);

  isSavingPostUrl.value = false;

  emit("updatePostUrl", updatedPost.postUrl, updatedPost._etag);
}

// Update post ================================================================
const postScheduleDateTime = ref<string | null>(null);
const isSavingScheduleDate = ref<boolean>(false);
const isScheduleWarningVisible = ref<boolean>(false);

onMounted(() => {
  if (props.isPostScheduleVisible && props.post.scheduledToPostAt) {
    postScheduleDateTime.value = DayjsExtended(props.post.scheduledToPostAt.localDateTime).format("YYYY-MM-DDTHH:mm");
  }
});
async function updatePostScheduleDate() {
  // Validate the selected date is not too soon
  const localDateTime = postScheduleDateTime.value ? DayjsExtended(postScheduleDateTime.value).format("YYYY-MM-DDTHH:mm") : null;
  if (localDateTime && isScheduledDateTooSoon(localDateTime)) {
    // Reset to original value (if possible)
    if (props.post.scheduledToPostAt) postScheduleDateTime.value = DayjsExtended(props.post.scheduledToPostAt.localDateTime).format("YYYY-MM-DDTHH:mm");

    // Show warning
    isScheduleWarningVisible.value = true;
    setTimeout(() => {
      isScheduleWarningVisible.value = false;
    }, MS_TO_SHOW_WARNING);

    return;
  }

  isSavingScheduleDate.value = true;

  // Passing in the local date time (without offset) to update the post schedule
  const updatedPost = await ShareStopSummaryRepo.updatePostScheduledAt(props.post, localDateTime);
  emit("updatePostSchedule", updatedPost);

  isSavingScheduleDate.value = false;
}

function formatScheduledDate(localDateTime: string): string {
  const dt = DayjsExtended(localDateTime);
  const format = dt.minute() === 0 ? "dddd [the] Do[,] ha" : "dddd [the] Do[,] h:mma";
  return dt.format(format);
}

function getTimeZoneAbbreviation(): string {
  try {
    if (!props.post?.scheduledToPostAt?.timeZone) return "";

    const timezoneCode = props.post.scheduledToPostAt.timeZone;
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezoneCode,
      timeZoneName: "short",
    }).formatToParts(new Date(props.post.scheduledToPostAt.localDateTime));
    return parts.find(p => p.type === "timeZoneName")?.value ?? "";
  } catch (error) {
    console.error("Error getting time zone abbreviation", error);
    return "ERR";
  }
}

function getStatusText(): string {
  const status = props.post?.status;
  if (!status) return "";
  if (props.post?.deletedDate) return "Deleted";
  const displayStatus = status === SocialPostStatus.Unscheduled ? SocialPostStatus.Draft : status;
  if (props.isUnusedPost && displayStatus === SocialPostStatus.Draft) {
    return "Unused";
  }
  return displayStatus;
}

// Toggle "Pick socials" modal ================================================
const isPickSocialsModalVisible = ref<boolean>(false);

// Re-run evals ===============================================================
const showEvalsRequestedIndicator = ref<boolean>(false);

async function reRunEvals() {
  if (!props.post?.id || !props.post?.customerId) return;
  await ShareStopSummaryRepo.rerunEvalsForPost(props.post.customerId, props.post.id);

  showEvalsRequestedIndicator.value = true;
  setTimeout(() => {
    showEvalsRequestedIndicator.value = false;
  }, 10000);
}
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// Share and dislike buttons ==================================================
.share-and-dislike-buttons {
  gap: 12px;
  display: flex;
  justify-content: space-between;

  &__share-button {
  }

  &__dislike-button {
    width: 50px;
  }
}

// Used mark ==================================================================
.used-mark {
  align-items: center;

  &__header {
    display: flex;
    align-items: center;
    margin-top: 12px;
  }

  &__title {
    margin-right: 4px;
    font-weight: 500;
  }

  &__icon {
  }

  &__link {
    margin-right: 5px;
    position: relative;
    z-index: 2;
    font-weight: 500;
  }

  &__add-link-form {
    gap: 8px;
    margin-right: 5px;
    display: flex;
    position: relative;
    z-index: 2;
  }

  &__text-input {
  }

  &__add-button {
  }
}

// Social logos row ===========================================================
.social-logos-row {
  flex-shrink: 0;

  &__ul {
    gap: 4px;
    padding: 0;
    display: flex;
    flex-wrap: nowrap;
    list-style: none;
    margin-bottom: 0;
    margin-top: 0;
  }

  &__logo-wrap {
    width: 21px;
    display: flex;
    align-items: center;
  }

  &__logo {
    width: 100%;

    &--inactive {
      opacity: 0.4;
      filter: grayscale(1);
    }
  }
}

// Post schedule field ========================================================
.post-schedule-field {
  @include standardFont;
  padding: 11px 0 0;
  border-top: 1px rgba(0, 0, 0, 0.15) solid;
  position: relative;
  z-index: 0;
  font-weight: 400;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  &__invisible-date-field-overlay {
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: 3;
    opacity: 0;
    pointer-events: none;
  }
}

// "Posted on" field ==========================================================
.posted-on-field {
  @include standardFont;
  display: flex;
  align-items: center;
  gap: 8px;

  &__text {
  }

  &__icons-row {
    padding: 0;
    margin: 0;
    display: flex;
    list-style: none;
  }

  &__icon {
  }
}

// Post preview section =======================================================
.post-preview-section {
  display: flex;
  flex-direction: column;

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &__post-snippet-wrap {
    flex-grow: 1;
    padding: 12px 3px 13px;
    border: 1px #d7d7d7 solid;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    background: white;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16);
    position: relative;
  }

  &__post-schedule-field {
    // margin: 0 0 11px;
  }

  &__posted-on-field {
  }

  &__social-logos-row {
    margin-bottom: 0;
  }

  &__status-stripe {
    position: absolute;
    top: -9px;
    right: 12px;
    z-index: 30;
  }

  &__post-snippet {
    flex-grow: 1;

    &--translucent {
      opacity: 0.5;
    }
  }

  &__post-snippet-textarea {
    scroll-behavior: smooth;
  }

  &__used-mark {
    margin: 10px 12px 0;
  }

  &__divider {
    width: calc(100% - 24px);
    margin: 0 12px 18px;
    border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
  }

  &__share-and-dislike-buttons {
    margin: 15px 13px 17px;
  }

  &__what-post-is-based-on {
    @include standardFont;
    padding: 11px 0 0;
    margin: 0 12px;
    border-top: 1px rgba(0, 0, 0, 0.1) solid;
  }

  &__footer-divider {
    margin-bottom: 10px;
    border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
  }
}
</style>
